import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useFloatingPanel, estimateAutocompletePanelHeight, floatingPanelStyle } from "@/lib/useFloatingPanel";

const MAX_OPTIONS = 60;

export function OrderFilterSelect({
  label,
  value,
  options,
  onChange,
  placeholder = "Alle",
  disabled,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);

  useEffect(() => {
    if (!open) setQuery(value);
  }, [value, open]);

  const sorted = useMemo(
    () => [...options].sort((a, b) => a.localeCompare(b, "de")),
    [options],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted.slice(0, MAX_OPTIONS);
    return sorted.filter((o) => o.toLowerCase().includes(q)).slice(0, MAX_OPTIONS);
  }, [sorted, query]);

  const truncated = sorted.length > MAX_OPTIONS && !query.trim();
  const showPanel = open && !disabled;
  const panelHeightEstimate = useMemo(
    () =>
      estimateAutocompletePanelHeight({
        optionCount: filtered.length,
        extraRows: 1,
        maxHeightCap: 280,
      }),
    [filtered.length],
  );
  const floating = useFloatingPanel(inputRef, showPanel, {
    gap: 4,
    maxHeightCap: 280,
    minBelow: 120,
    contentHeightEstimate: panelHeightEstimate,
  });

  function pick(next: string) {
    onChange(next);
    setQuery(next);
    setOpen(false);
    inputRef.current?.blur();
  }

  function clear() {
    onChange("");
    setQuery("");
    inputRef.current?.focus();
  }

  return (
    <div className="min-w-0 space-y-1">
      <label className="eh-label text-xs" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="search"
          autoComplete="off"
          disabled={disabled}
          className={clsx("eh-input !py-1.5 pe-8 text-sm", value && !open && "font-medium")}
          placeholder={placeholder}
          value={open ? query : value || query}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setQuery(value);
          }}
          onBlur={() => {
            if (closeTimer.current) window.clearTimeout(closeTimer.current);
            closeTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setQuery(value);
            }
          }}
        />
        {value ? (
          <button
            type="button"
            className="absolute inset-y-0 end-1 my-auto flex h-6 w-6 items-center justify-center rounded-md text-label-tertiary hover:bg-fill-tertiary hover:text-label"
            aria-label={`${label} zurücksetzen`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
          >
            ×
          </button>
        ) : null}
        {showPanel && floating
          ? createPortal(
              <ul
                className="z-[900] overflow-y-auto rounded-ios border border-separator bg-card py-1 shadow-ios"
                style={floatingPanelStyle(floating)}
                role="listbox"
                aria-label={label}
              >
                <li>
                  <button
                    type="button"
                    className={clsx(
                      "w-full px-3 py-2 text-start text-sm hover:bg-fill-tertiary",
                      !value && "bg-fill-tertiary font-medium text-accent",
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick("")}
                  >
                    {placeholder}
                  </button>
                </li>
                {filtered.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      className={clsx(
                        "w-full px-3 py-2 text-start text-sm hover:bg-fill-tertiary",
                        value === opt && "bg-fill-tertiary font-medium text-accent",
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(opt)}
                    >
                      {opt}
                    </button>
                  </li>
                ))}
                {!filtered.length ? (
                  <li className="px-3 py-2 text-sm text-label-tertiary">Keine Treffer</li>
                ) : null}
                {truncated ? (
                  <li className="border-t border-separator px-3 py-1.5 text-xs text-label-tertiary">
                    {sorted.length} Einträge — bitte eingrenzen
                  </li>
                ) : null}
              </ul>,
              document.body,
            )
          : null}
      </div>
    </div>
  );
}
